/**
 * Auth API module
 * Wraps baobab-api /auth routes.
 *
 * Flow: our backend calls Supabase internally and returns Supabase tokens.
 * After any successful auth call we hydrate the Supabase client with
 * `supabase.auth.setSession()` so the request interceptor in client.ts
 * can always read a valid session.
 */

import { supabase } from '@/lib/supabase'
import client from './client'
import type { ApiResponse } from './client'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthTokens {
  access_token: string
  refresh_token: string
  expires_in: number
}

export interface BackendUser {
  id: string
  supabase_id: string
  phone: string | null
  email: string | null
  country_code: string | null
  preferred_language: string
  subscription_tier: 'free' | 'basic' | 'standard' | 'premium'
  created_at: string
}

export interface AuthMeResponse {
  user: BackendUser
  profile: {
    id: string
    display_name: string
    avatar_url: string | null
  } | null
  subscription: {
    tier: string
    status: string
    expires_at: string
  } | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Hydrate Supabase client with tokens returned by the backend. */
async function hydrateSession(tokens: AuthTokens) {
  await supabase.auth.setSession({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
  })
}

// ─── API calls ────────────────────────────────────────────────────────────────

/**
 * Start phone registration (sends OTP) or email sign-up.
 * For phone: pass { phone }
 * For email: pass { email, password, country_code?, preferred_language? }
 */
export async function register(payload: {
  phone?: string
  email?: string
  password?: string
  country_code?: string
  preferred_language?: string
  full_name?: string
}) {
  const { data } = await client.post<{
    message: string
    user_id: string
    profile_id: string
    session_token: string | null
    refresh_token: string | null
  }>('/auth/register', payload)
  // Email path: API returns tokens immediately after signUp.
  // Phone path: session_token is null — tokens come after OTP verify.
  if (data.session_token && data.refresh_token) {
    await hydrateSession({
      access_token: data.session_token,
      refresh_token: data.refresh_token,
      expires_in: 3600,
    })
  }
  return data
}

/**
 * Verify OTP sent to phone and complete sign-in.
 * Returns Supabase tokens — session is hydrated automatically.
 */
export async function verifyOtp(phone: string, otp_code: string, full_name?: string) {
  const { data } = await client.post<{
    access_token: string
    refresh_token: string
    user_id: string
    profile_id: string
  }>('/auth/verify-otp', { phone, otp_code, ...(full_name ? { full_name } : {}) })
  await hydrateSession({ access_token: data.access_token, refresh_token: data.refresh_token, expires_in: 3600 })
  return data
}

/**
 * Email + password login.
 * Returns Supabase tokens — session is hydrated automatically.
 */
export async function login(email: string, password: string) {
  const { data } = await client.post<{
    access_token: string
    refresh_token: string
    user_id: string
    profile_id: string
  }>('/auth/login', { email, password })
  if (process.env.NODE_ENV !== 'production') {
    console.log('[login] raw response:', data)
  }
  await hydrateSession({ access_token: data.access_token, refresh_token: data.refresh_token, expires_in: 3600 })
  return data
}

/**
 * Trigger a WhatsApp OTP for the given phone number.
 * Works for both new and existing accounts — Supabase signInWithOtp is
 * idempotent. Follow up with verifyOtp() to complete sign-in.
 */
export async function sendOtp(phone: string, country_code?: string) {
  const { data } = await client.post<ApiResponse<{ message: string; phone: string }>>(
    '/auth/send-otp',
    { phone, ...(country_code ? { country_code } : {}) }
  )
  return data
}

/**
 * Refresh the access token using a refresh token.
 */
export async function refreshSession(refresh_token: string) {
  const { data } = await client.post<ApiResponse<{ access_token: string; expires_in: number }>>(
    '/auth/refresh',
    { refresh_token }
  )
  return data
}

/**
 * Clear the local Supabase session. Client-side only — no backend call needed.
 * The backend /auth/logout no longer validates the token (avoids 500 on
 * expired/malformed JWTs). Supabase session is cleared locally here.
 */
export async function logout() {
  await supabase.auth.signOut()
}

/**
 * Fetch the authenticated user + active profile + subscription.
 * Lightweight alternative to GET /users/me — used for the nav bar.
 */
export async function me() {
  const { data } = await client.get<ApiResponse<AuthMeResponse>>('/auth/me')
  return data
}
